"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthError, InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { User } from "@microsoft/microsoft-graph-types";

import { Authorization, AuthContextType } from "@/types/auth";
import { GraphTenant } from "@/types/microsoft";

import { apiRequest, loginRequest } from "@/lib/auth/msal";
import { httpRequest } from "@/lib/http";

import { useUi } from "@/context/util/ui";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultMetadata = {
  needsAuth: false,
  title: "Account Utilities",
  url: "/",
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const { metadata, setMetadata, setAvatar } = useUi();

  const [authorization, setAuthorization] = useState<Authorization | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);
  const [upn, setUpn] = useState<string | null>(null);

  const account = useMemo(() => {
    return accounts.find(
      (acc) => acc.tenantId === process.env.NEXT_PUBLIC_TENANT_ID
    );
  }, [accounts]);

  const activeAccount = instance.getActiveAccount();

  const msalReady =
    inProgress === InteractionStatus.None &&
    account != null &&
    activeAccount != null &&
    activeAccount.localAccountId === account.localAccountId;

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    // If no matching account exists, redirect to login
    if (!account) {
      console.warn(
        "No matching account for required tenant. Redirecting to login."
      );

      instance.loginRedirect(loginRequest);

      return;
    }
  }, [account, inProgress, instance]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || !account) return;

    const currentActive = instance.getActiveAccount();

    if (
      !currentActive ||
      currentActive.localAccountId !== account.localAccountId
    ) {
      instance.setActiveAccount(account);
    }

    setUpn(account.username);
  }, [account, instance, inProgress]);

  // get a valid access token on-demand
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (inProgress !== InteractionStatus.None) {
      console.warn("MSAL interaction still in progress, skipping token fetch.");
      return null;
    }

    const activeAccount = instance.getActiveAccount();

    if (
      !activeAccount ||
      activeAccount.tenantId !== process.env.NEXT_PUBLIC_TENANT_ID
    ) {
      console.warn("Invalid tenant or no active account. Skipping.");
      return null; // page will reload
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiRequest,
        account: activeAccount,
      });

      return tokenResponse.accessToken;
    } catch (error) {
      if (
        error instanceof AuthError &&
        [
          "interaction_required",
          "login_required",
          "consent_required",
          "token_refresh_required",
          "monitor_window_timeout",
        ].includes(error.errorCode)
      ) {
        console.warn(
          "Silent auth failed. Will not redirect due to active interaction."
        );
        return null;
      }

      console.error("Token fetch failed:", error);
      throw error;
    }
  }, [instance, inProgress]);

  const getAvatar = useCallback(
    async (upn: string, tenant: GraphTenant = "employee") => {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const url = `/account-utilities/api/microsoft/graph/user/photo?upn=${upn}&tenant=${tenant}`;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await httpRequest<string>(url, {
          method: "GET",
          headers,
        });

        if (!response.data) throw new Error("Empty http response");

        return response.data;
      } catch (err) {
        console.error("Failed to get user's avatar photo:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      }
    },
    [getAccessToken]
  );

  const handleSetAvatar = useCallback(
    async (upn: string) => {
      const newAvatar = await getAvatar(upn);
      if (newAvatar) setAvatar(newAvatar);
    },
    [getAvatar]
  );

  useEffect(() => {
    if (!upn || !msalReady) return;
    handleSetAvatar(upn);
  }, [handleSetAvatar, msalReady, upn]);

  useEffect(() => {
    async function getAuthorization() {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const url = "/account-utilities/api/help-desk/security/authorization";

        const headers = { Authorization: `Bearer ${token}` };
        const method = "GET";

        const requestInit: RequestInit = { headers, method };

        const newAuthorization = (
          await httpRequest<Authorization>(url, requestInit)
        ).data;

        if (!newAuthorization) throw new Error("Empty http response");

        setAuthorization(newAuthorization);
      } catch (error) {
        console.error("Failed to get user authorization:", error);
      }
    }

    if (!metadata.needsAuth || authorization !== null || !msalReady) return;

    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) return; // wait for redirect to finish

    getAuthorization();
  }, [authorization, metadata.needsAuth, msalReady]);

  async function getAuthenticationMetadata(
    upn: string,
    tenant: GraphTenant = "employee"
  ) {
    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = `/account-utilities/api/microsoft/graph/user?upn=${upn}&tenant=${tenant}&beta=true&$select=lastPasswordChangeDateTime,signInActivity`;

      const headers = { Authorization: `Bearer ${token}` };
      const method = "GET";

      const requestInit: RequestInit = { headers, method };

      const user = (await httpRequest<User>(url, requestInit)).data;

      if (!user) throw new Error("Empty http response");

      const lastLogon = user.signInActivity?.lastSignInDateTime;
      const lastPasswordSet = user.lastPasswordChangeDateTime;

      return { lastLogon, lastPasswordSet };
    } catch (error) {
      console.error("Failed to get user's azure authentication data:", error);
      return {};
    }
  }

  return (
    <AuthContext.Provider
      value={{
        authorization,
        getAccessToken,
        getAuthenticationMetadata,
        getAvatar,
        error,
        msalReady,
        setAuthorization,
        upn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthContext");
  return ctx;
};
